import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ListChecks, Check, Coins, Sparkles, RefreshCw, CalendarClock, CalendarDays } from "lucide-react";
import { useSmartBack } from "../utils/useSmartBack.js";
import { toast } from "../utils/toast.js";
import { BACKEND_URL as GREENWICH_BACKEND_URL } from "../utils/network.js";
import "./TasksPage.css";

const API_URL = `${GREENWICH_BACKEND_URL}`;

const FREQUENCY_ICON = { once: Sparkles, daily: CalendarClock, monthly: CalendarDays };

function TasksPage() {
  const { t } = useTranslation();
  const goBack = useSmartBack("/levels");
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completingId, setCompletingId] = useState(null);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/gamification/tasks`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setTasks(Array.isArray(data.tasks) ? data.tasks : []);
    } catch {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTasks(); }, []);

  const completeTask = async (task) => {
    setCompletingId(task._id);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/gamification/tasks/${task._id}/complete`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast(data.error || t("pages.tasks.completeFailed"));
        return;
      }
      setTasks((prev) => prev.map((x) => (x._id === task._id ? { ...x, completed: true } : x)));
      toast(
        task.greencoinReward
          ? t("pages.tasks.completedWithCoins", { xp: task.xpReward, coins: task.greencoinReward })
          : t("pages.tasks.completed", { xp: task.xpReward })
      );
    } catch {
      toast(t("pages.tasks.completeFailed"));
    } finally {
      setCompletingId(null);
    }
  };

  const frequencyLabel = (f) =>
    f === "daily" ? t("pages.tasks.frequencyDaily") : f === "monthly" ? t("pages.tasks.frequencyMonthly") : t("pages.tasks.frequencyOnce");

  return (
    <div className="tsk-page">
      <div className="tsk-shell">
        <div className="tsk-hero">
          <div className="tsk-hero-icon"><ListChecks size={26} /></div>
          <h1 className="tsk-hero-title">{t("pages.tasks.title")}</h1>
          <p className="tsk-hero-sub">{t("pages.tasks.subtitle")}</p>
        </div>

        {loading ? (
          <div className="tsk-loading"><RefreshCw size={18} className="tsk-spin" /> {t("pages.tasks.loading")}</div>
        ) : tasks.length === 0 ? (
          <div className="tsk-empty">
            <Sparkles size={28} className="tsk-empty-icon" />
            <p>{t("pages.tasks.empty")}</p>
          </div>
        ) : (
          <div className="tsk-list">
            {tasks.map((task) => {
              const FreqIcon = FREQUENCY_ICON[task.frequency] || Sparkles;
              return (
                <div key={task._id} className={`tsk-card${task.completed ? " tsk-card-done" : ""}`}>
                  <div className="tsk-card-icon"><FreqIcon size={18} /></div>
                  <div className="tsk-card-body">
                    <div className="tsk-card-top">
                      <h3>{task.title}</h3>
                      <span className="tsk-freq-badge">{frequencyLabel(task.frequency)}</span>
                    </div>
                    {task.description && <p className="tsk-card-desc">{task.description}</p>}
                    <div className="tsk-card-reward">
                      <span className="tsk-xp-pill"><Sparkles size={12} /> {t("pages.tasks.xpAmount", { amount: task.xpReward })}</span>
                      {task.greencoinReward > 0 && (
                        <span className="tsk-coin-pill"><Coins size={12} /> {task.greencoinReward}</span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="tsk-complete-btn"
                    disabled={task.completed || completingId === task._id}
                    onClick={() => completeTask(task)}
                  >
                    {task.completed ? (
                      <><Check size={14} /> {t("pages.tasks.doneBtn")}</>
                    ) : completingId === task._id ? (
                      t("pages.tasks.savingEllipsis")
                    ) : (
                      t("pages.tasks.completeBtn")
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <button className="tsk-back-btn" onClick={goBack}>
          <ArrowLeft size={16} /> {t("pages.tasks.back")}
        </button>
      </div>
    </div>
  );
}

export default TasksPage;
